
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Card } from "react-bootstrap";

import { GanttColumn } from "../../../Components/GantV2/types";

import { useAuth } from "../../../Hooks/UseAuth";
import useTranslation from "../../../Hooks/UseTranslation";
import { useProject } from "../../../Hooks/UseProject";
import { useProgrammation } from "../../../Hooks/UseProgrammation";
import { useProjection } from "@/Hooks/ProjectionProvider";
import { useTeams } from "@/Hooks/UseTeam";

import { getWeekNavigator } from "../../../Helpers/LookaheadHelper";
import TeamsHelper from "../../../Helpers/TeamsHelper";
import { maxDate } from "@/Helpers/DateFunctions";
import {
  IGetItemsListOptions,
  filterByColorCodes,
  filterByGroups,
  filterByStatus,
  filterByTypes,
  filterByUsers,
  getCollapsedGroups,
  getCollapsedTasks,
  getGanttItems,
  getNonWorkingDays,
  getWeeklyResult,
  saveCollapsedGroups,
  saveCollapsedTasks
} from "../../../Helpers/ProgrammationHelper";
import History from "../../../Helpers/History";

import ProjectsActions from "../../../Redux/Actions/ProjectsActions";
import InterfaceActions from "../../../Redux/Actions/InterfaceActions";
import { regenerateWeekly, setWeeklyAction, setWeeklyCollapsedGroupsAction, setWeeklyCollapsedTasksAction, setWeeklyGanttItemsAction } from "../../../Redux/Actions/WeeklyActions";

import { getStatusName } from "../../../Components/TaskManager/TaskEditorHelper";
import Gantt from "../../../Components/GantV2/Gantt";
import TaskFilters from "../../../Components/TaskFilters/TaskFilters";
import UserAvatar from "../../../Components/UserAvatar/UserAvatar";
import Icon from "../../../Components/Icon/Icon";
import PdfExportModal from "../../../Components/PdfExportModal/PdfExportModal";
import ProgrammationManager from "../../../Components/ProgrammationManager/ProgrammationManager";
import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentHeader from "@/Layouts/MainLayout/components/ContentHeader";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";

const WeeklyPage = (props: any) => {

  const { t } = useTranslation()

  const sidebarRefBtn = useRef<HTMLButtonElement>(null)

  const dispatch = useDispatch()

  const { userData, ability } = useAuth()

  const { project_id, project, projectLoaded } = useProject()
  const { programmationLoaded, loadProgrammation, openItem } = useProgrammation()
  const { projectedEndDate, extendedNonWorkingDays } = useProjection()

  const { teams, allInternalProfiles } = useTeams()

  const week = props.match.params.week

  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const weekly = useSelector((state: any) => state.WeeklyReducer)

  console.log('weekly', weekly)

  const teamsMembersIds = useMemo(() => TeamsHelper.getAllMembersFromProject(teams).map((user: any) => user._id), [ teams ])

  const [ showTaskFilters, setShowTaskFilters ] = useState(false)

  const savedFilters = localStorage.getItem(`${project_id}-weekly-filters`)

  const [ filters, setFilters ] = useState(savedFilters ? JSON.parse(savedFilters) : {
    user: '',
    state: '',
    team: '',
    group: '',
    code: '',
    type: '',
    start: '',
    end: '',
  })

  const [ mySettings, setMySettings ] = useState(project?.mySettings)
  const [ showSidebar, setShowSidebar ] = useState(false)

  const navigator = useMemo(() => {

    if (!project._id) return {
      prev: '',
      next: '',
      startDay: '',
      endDay: '',
      week: 0,
      range: []
    }

    return getWeekNavigator({
      projectStart: project.startsAt,
      projectEnd: maxDate(project.realEnd, projectedEndDate) || project.realEnd,
      weeksSize: 1,
      week
    })
  }, [ project, week, projectedEndDate ])

  const navigateToWeek = (week?: any) => {

    dispatch(regenerateWeekly())

    if (week) {

      History.push(`/project/${project_id}/programmation-weekly/${week}`)
    } else {

      History.push(`/project/${project_id}/programmation-weekly`)
    }
  }

  const nonWorkingDays = useMemo(() => {

    if (!project._id) return []

    return getNonWorkingDays(weekly.start, weekly.end, project)
  }, [ project._id, weekly.start, weekly.end ])

  const [ showPdfExportModal, setShowPdfExportModal ] = useState(false)

  const columns: GanttColumn[] = [
    {
      label: t('WEEKLY:TypeColumn'),
      field: 'type',
      width: 50,
      render: (field: any) => !field ? null : field.substring(0, 1).toUpperCase()
    },
    {
      label: t('WEEKLY:ColorCodeColumn'),
      field: 'colorCodeName',
      width: 60,
    },
    {
      label: t('WEEKLY:PositionColumn'),
      field: 'position',
      width: 100,
    },
    {
      label: t('WEEKLY:TitleColumn'),
      field: 'name',
      width: 300,
    },
    {
      label: t('WEEKLY:StatusColumn'),
      field: 'status',
      width: 100,
      render: (field: any) => getStatusName(field)
    },
    {
      label: t('WEEKLY:StartsAtColumn'),
      field: 'startsAt',
      width: 80,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:EndsAtColumn'),
      field: 'endsAt',
      width: 80,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:ResponsiblesColumn'),
      field: 'responsibles',
      width: 80,
      render: (field: any, item: any) => {

        const isField = !!field
        const isFieldArray = isField && Array.isArray(field)

        if (isField && isFieldArray) {

          const responsibles = field.filter((user: any) => teamsMembersIds.includes(user._id))

          return responsibles.map((user: any) => <UserAvatar key={user._id} user={user} size="xs" />)
        }

        return null
      }
    },
    {
      label: t('WEEKLY:ReleasesAtColumn'),
      field: 'releasesAt',
      width: 80,
      render: (field: any) => field ? moment.utc(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:ActualStartColumn'),
      field: 'startedAt',
      width: 90,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:ActualEndColumn'),
      field: 'endedAt',
      width: 90,
      render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
    },
    {
      label: t('WEEKLY:CommentsColumn'),
      field: 'comments',
      width: 150,
      render: (field: any) => {

        if (!field) return null

        return `${field.comment}...`
      }
    }
  ]

  const loadWeekly = (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      const responsibles = task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp))

      return {
        ...task,
        responsibles,
        internalProfiles: task.internalProfiles.map((resp: any) => allInternalProfiles.find((profile: any) => profile._id === resp)),
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      navigator,
      nonWorkingDays: project.nonWorkingDays,
    }

    let weeklyResult = getWeeklyResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      weeklyResult = filterByUsers(weeklyResult, users_ids)
    } else if (newFilters.user)
      weeklyResult = filterByUsers(weeklyResult, newFilters.user.split(','))

    if (newFilters.group) {

      weeklyResult = filterByGroups(weeklyResult, newFilters.group.split(','))
    } else if (newFilters.code)
      weeklyResult = filterByColorCodes(weeklyResult, newFilters.code.split(','))

    if (newFilters.type)
      weeklyResult = filterByTypes(weeklyResult, newFilters.type.split(','))

    if (newFilters.state)
      weeklyResult = filterByStatus(weeklyResult, newFilters.state.split(','))

    const collapsedGroups = getCollapsedGroups(project_id, 'weekly')
    const collapsedTasks = getCollapsedTasks(project_id, 'weekly')

    dispatch(setWeeklyAction(weeklyResult, navigator.startDay, navigator.endDay, navigator.week, collapsedGroups, collapsedTasks))
  }

  const hasActiveFilters = () => {

    const values = Object.values(filters)
    const found = values.findIndex(f => f !== '')

    return !!(found >= 0)
  }

  const handleChangeFilters = (filters: any) => {

    localStorage.setItem(`${project_id}-weekly-filters`, JSON.stringify(filters))

    loadWeekly(filters)
    setFilters(filters)
  }

  const handleEditItem = async (item_id: string) => {

    openItem({ item_id, schema: 'weekly' })
  }

  const handleToggleCollapse = async (group_id: string) => {

    const collapsedGroups = getCollapsedGroups(project_id, 'weekly')

    if (collapsedGroups.includes(group_id)) {

      collapsedGroups.splice(collapsedGroups.indexOf(group_id), 1)
    } else {

      collapsedGroups.push(group_id)
    }

    saveCollapsedGroups(project_id, 'weekly', collapsedGroups)

    dispatch(setWeeklyCollapsedGroupsAction(collapsedGroups))
  }

  const handleToggleCollapseTask = async (task_id: string) => {

    const collapsedTasks = getCollapsedTasks(project_id, 'weekly')

    if (collapsedTasks.includes(task_id)) {

      collapsedTasks.splice(collapsedTasks.indexOf(task_id), 1)
    } else {

      collapsedTasks.push(task_id)
    }

    saveCollapsedTasks(project_id, 'weekly', collapsedTasks)

    dispatch(setWeeklyCollapsedTasksAction(collapsedTasks))
  }

  const handleShowWorkflows = (groups: string[], tasks: any[]) => {

    saveCollapsedGroups(project_id, 'weekly', groups)
    saveCollapsedTasks(project_id, 'weekly', tasks)

    dispatch(setWeeklyCollapsedGroupsAction(groups))
    dispatch(setWeeklyCollapsedTasksAction(tasks))
  }

  const toggleSidebar = (target?: any) => {

    if (!target) {

      if (showSidebar) updateSettings()

      setShowSidebar(!showSidebar)
    } else {

      if (sidebarRefBtn.current) {

        const isButtonClicked = sidebarRefBtn.current.contains(target)

        if (!isButtonClicked) {

          // trigger click because if not mySettings is not updated
          sidebarRefBtn.current.click()
        }
      }
    }
  }

  const handleUpdateSetting = async (key: string, value: any) => {

    let field = key

    if (field === 'dayWidth') {

      field = 'ganttWeeklyColumnSize'
    } else if (field === 'rowHeight') {

      field = 'ganttWeeklyRowSize'
    } else if (field === 'hideNonWorkingDays') {

      field = 'ganttWeeklyHideNonWorkingDays'
    } else if (field === 'showVerticalBorders') {

      field = 'ganttWeeklyShowVerticalBorders'
    } else if (field === 'columnsHidden') {

      field = 'ganttHideWeeklyFields'
    } else if (field === 'divisorPosition') {

      field = 'ganttWeeklyDivisorPosition'
    }

    await updateSettings({
      ...mySettings,
      [ field ]: value
    })
  }

  const updateSettings = async (settings = mySettings) => {

    let areDifferent = false

    for (const key in settings) {

      if (key !== 'setted' && settings[ key ] !== project.mySettings[ key ]) {

        areDifferent = true
      }
    }

    if (areDifferent) {

      await dispatch(ProjectsActions.updateSettings(project.mySettings._id, settings))
    }

    setMySettings(settings)
  }

  useEffect(() => {

    if (projectLoaded) {


      setMySettings(project.mySettings)
    }
  }, [ projectLoaded ])

  useEffect(() => {

    if (projectLoaded) {

      if (programmation.loadStatus === 'none') {

        loadProgrammation()
      }
    }
  }, [ projectLoaded, programmation.loadStatus ])

  useEffect(() => {

    if (!programmationLoaded) return

    if (weekly.loadStatus === 'none') {

      loadWeekly()
    }
  }, [ programmationLoaded, weekly.loadStatus ])

  // ganttitems
  useEffect(() => {

    if (weekly.loadStatus !== 'loaded') return
    if (weekly.loadGanttItemsStatus !== 'none') return

    const options: IGetItemsListOptions = {
      itemsList: weekly.result,
      collapsedGroups: weekly.collapsedGroups,
      collapsedTasks: weekly.collapsedTasks,
      schema: 'weekly',
      nonWorkingDays: extendedNonWorkingDays,
      stretchUpToday: mySettings?.stretchUpToday,
      moveToScheduledDays: mySettings?.moveToScheduledDays,
      showOnlyThisUserTasks: project.mySettings.showOnlyMyTasks ? userData?.id : undefined,
      week: navigator.week,
      ganttStart: weekly.start,
      ganttEnd: weekly.end,
    }

    const ganttItems = getGanttItems(options);

    // cortamos las barras de los items que se salen del rango de fechas
    ganttItems.forEach((item: any) => {

      item.barStart = moment(item.barStart).isBefore(moment(weekly.start)) ? weekly.start : item.barStart;
      item.barEnd = moment(item.barEnd).isAfter(moment(weekly.end)) ? weekly.end : item.barEnd;

      item.bars?.forEach((bar: any) => {

        bar.barStart = moment(bar.barStart).isBefore(moment(weekly.start)) ? weekly.start : bar.barStart;
        bar.barEnd = moment(bar.barEnd).isAfter(moment(weekly.end)) ? weekly.end : bar.barEnd;
      })
    })

    dispatch(setWeeklyGanttItemsAction(ganttItems))
  }, [ weekly.loadGanttItemsStatus, weekly.result, mySettings ]);

  useEffect(() => {

    const taskListLoaded = weekly.loadStatus === 'loaded'

    if (!projectLoaded || !taskListLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }

  }, [ projectLoaded, weekly.loadStatus ])

  return (
    <ProjectLayout menuName="programmation" subMenuName="weekly">
      <ContentHeader
        className="pl4-programmation-header"
        title={t('NAVIGATION:Programmation')}
        subtitle={`${t('WEEKLY:Title')} · ${project.name || ''}`}
      >
        {/* <div className='ul-widget__head-label flex-grow-1'>
          <h3 className="ul-widget__head-title">
            <span>
              {t('WEEKLY:Title')}
            </span>
            {(weekly.start !== '' && weekly.end !== '') && (
              <small className="d-block d-sm-inline">
                ({t('WEEKLY:DateFrom')} {' '}
                {moment(weekly.start).format('DD/MM/YYYY')} {' '}
                {t('WEEKLY:DateTo')} {' '}
                {moment(weekly.end).format('DD/MM/YYYY')})
              </small>
            )}
          </h3>
        </div> */}
        <div className="ul-widget__head-toolbar pl4-programmation-toolbar">
          <button className={`btn btn-link btn-link-header pl4-programmation-action ${weekly.loadStatus === 'loading' ? 'active' : ''} d-inline-block`}
              onClick={() => loadProgrammation()}
              disabled={weekly.loadStatus === 'loading'}
            >
              <Icon name="sync" fas fw spin={weekly.loadStatus === 'loading'} />
              <span className="d-none d-lg-inline"> {t('WEEKLY:RefreshButton')}</span>
            </button>
          <button className="btn btn-link btn-link-header pl4-programmation-action pl4-programmation-action--period"
            onClick={() => navigateToWeek(navigator.prev)}
            disabled={navigator.prev === ''}
          >
            <i className="icon-anterior"></i>
            <span className="d-none d-lg-inline"> {t('WEEKLY:PreviousButton')}</span>
          </button>
          <button className="btn btn-link btn-link-header pl4-programmation-action pl4-programmation-action--period"
            onClick={(e) => navigateToWeek()}
          >
            <i className="icon-semana_actual"></i>
          </button>
          <button className="btn btn-link btn-link-header pl4-programmation-action pl4-programmation-action--period"
            onClick={(e) => navigateToWeek(navigator.next)}
            disabled={navigator.next === ''}
          >
            <span className="d-none d-lg-inline">{t('WEEKLY:NextButton')} </span>
            <i className="icon-siguiente"></i>
          </button>
          <button className={`btn btn-link btn-link-header pl4-programmation-action ${hasActiveFilters() ? 'active' : ''}`}
            onClick={() => setShowTaskFilters(true)}
          >
            <span className="icon-filtro"></span>
            <span className="d-none d-lg-inline"> {t('WEEKLY:FilterButton')}</span>
          </button>
          <button className="btn btn-link btn-link-header pl4-programmation-action"
            ref={sidebarRefBtn}
            onClick={() => setShowPdfExportModal(true)}
          >
            <i className="fas fa-download"></i>
            <span className="d-none d-lg-inline"> {t('WEEKLY:ExportButton')}</span>
          </button>
          <button className={`btn btn-link btn-link-header pl4-programmation-action ${showSidebar ? 'active' : ''}`}
            ref={sidebarRefBtn}
            onClick={() => toggleSidebar()}
          >
            <span className="icon-configuracion"></span>
            <span className="d-none d-lg-inline"> {t('WEEKLY:SettingsButton')}</span>
          </button>
        </div>
        <div className='ul-widget__head-nav'>

        </div>
      </ContentHeader>
      <ContentBody noScroll>
        <Card className="table-card" style={{ height: 'calc(100% - 8px)' }}>
          {project._id && (
            <Gantt
              schema="weekly"
              start={weekly.start}
              end={weekly.end}
              items={weekly.ganttItems}
              columns={columns}
nonWorkingDays={extendedNonWorkingDays}
              dayWidth={mySettings?.ganttWeeklyColumnSize || 100}
              maxDayWidth={240}
              rowHeight={mySettings?.ganttWeeklyRowSize || 32}
              maxRowHeight={160}
              hideNonWorkingDays={mySettings?.ganttWeeklyHideNonWorkingDays || false}
              showVerticalBorders={mySettings?.ganttWeeklyShowVerticalBorders || false}
              divisorPosition={mySettings?.ganttWeeklyDivisorPosition}
              useCodeColors={mySettings?.useCodeColors || false}
              stretchUpToday={mySettings?.stretchUpToday || false}
              moveToScheduledDays={mySettings?.moveToScheduledDays || false}
              columnsHidden={mySettings?.ganttHideWeeklyFields || []}
              showSidebar={showSidebar}
              onSelectItem={handleEditItem}
              onToggleCollapse={handleToggleCollapse}
              onToggleCollapseTask={handleToggleCollapseTask}
              onShowWorkflows={handleShowWorkflows}
              onClickOutside={toggleSidebar}
              onUpdateSetting={handleUpdateSetting}
              sortEnabled={false}
              lineBaseTasks={false}
            />
          )}
        </Card>
      </ContentBody>

      <TaskFilters
        show={showTaskFilters}
        type="weekly"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowTaskFilters(false)}
      />

      <ProgrammationManager taskList={weekly.result} />

      <PdfExportModal
        show={showPdfExportModal}
        plan="weekly"
        navigator={navigator}
        project_id={showPdfExportModal ? project_id : undefined}
        items={weekly.ganttItems}
        nonWorkingDays={nonWorkingDays}
        users={programmation.users}
        filters={filters}
        columns={columns.map((column: any) => {

          let key = column.field

          if (key === 'position') {
            key = 'id'
          } else if (key === 'name') {
            key = 'title'
          }

          return {
            key,
            label: column.label,
            width: column.width
          }
        })}
        collapsedGroups={weekly.collapsedGroups}
        collapsedTasks={weekly.collapsedTasks}
        onHide={() => setShowPdfExportModal(false)}
      />
    </ProjectLayout>
  )
}

export default WeeklyPage;
